// Crayo frontend -> backend API client.
//
// WHY one file:
//   Every endpoint in .planning/API.md is routed through `request(...)`.
//   Centralising fetch options means we set the base URL, Accept header,
//   and error envelope handling once, not per call. Components call
//   named helpers (`createSession`, `getOrder`, ...) and never see fetch
//   details.
//
// WHY `VITE_API_BASE` rather than a hardcoded URL:
//   HANDOFF-OVERNIGHT-ENV.md §1 requires the frontend to talk to whatever
//   Cloud Run URL Forge deploys (and eventually `https://api.crayo.ro`
//   once the domain is bought). Cloudflare Pages env var supplies it at
//   build time. Dev uses localhost:8787 (Hono dev server per API.md).
//
// WHY we localise errors in Romanian here:
//   The API contract returns Romanian user-safe strings already
//   (`error.message`). But network-level failures (offline, CORS,
//   5xx without body) never make it to the contract, so this file owns
//   the Romanian fallback wording. Components just toast whatever they
//   receive.

import * as Sentry from '@sentry/react';

import { getUtmParams } from './utm-capture.js';

/**
 * Base URL for the Crayo backend API.
 * - Override (any environment): set VITE_API_BASE at build time.
 * - Production fallback:        the live Cloud Run URL — used if no
 *                               VITE_API_BASE is set during the prod build.
 *                               Will be replaced by https://api.crayo.ro
 *                               when the subdomain is wired.
 * - Dev fallback:               http://localhost:8787 (Hono dev server).
 */
const PROD_API_FALLBACK = 'https://crayo-backend-p7bzmuvtdq-ey.a.run.app';

export const API_BASE =
  (import.meta.env.VITE_API_BASE && import.meta.env.VITE_API_BASE.replace(/\/$/, '')) ||
  (import.meta.env.PROD ? PROD_API_FALLBACK : 'http://localhost:8787');

// Expose on window for legacy components that pick values off globals.
if (typeof window !== 'undefined') {
  window.CRAYO_API_BASE = API_BASE;
}

/**
 * Wrap fetch with:
 *   - correct base URL
 *   - JSON body serialization for non-FormData payloads
 *   - uniform Romanian error messages on network / 5xx failures
 *   - unwrap of the `{ error: { code, message } }` API envelope
 *
 * Returns the parsed JSON on 2xx. Throws `ApiError` on everything else.
 */
async function request(path, { method = 'GET', body, signal, headers } = {}) {
  const url = `${API_BASE}${path}`;
  const opts = {
    method,
    // Omit credentials so we do not accidentally share cookies cross-origin.
    // Auth-scoped endpoints rely on the unguessable session/order id (per API.md).
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
      ...(headers || {}),
    },
    signal,
  };

  if (body !== undefined && body !== null) {
    if (body instanceof FormData) {
      opts.body = body;
      // Do NOT set Content-Type; the browser fills the multipart boundary.
    } else {
      opts.body = JSON.stringify(body);
      opts.headers['Content-Type'] = 'application/json';
    }
  }

  let response;
  try {
    response = await fetch(url, opts);
  } catch (err) {
    // Network error (offline, DNS, CORS preflight failure, etc.). Fetch
    // never distinguishes these at the API level, so we treat them all
    // the same way: the user sees a friendly message, we log the
    // real reason to the console for debugging, and Sentry sees the
    // underlying cause with the request URL as a tag.
    // eslint-disable-next-line no-console
    console.error('[crayo api]', method, url, err);
    Sentry.captureException(err, { tags: { kind: 'network', method, path } });
    throw new ApiError(
      'network.unreachable',
      'Nu am reușit să ne conectăm la server. Verifică conexiunea și reîncearcă.',
      err
    );
  }

  // 204 No Content
  if (response.status === 204) return null;

  // Try to parse JSON even on error responses; the API contract requires
  // the error envelope for 4xx/5xx.
  let payload = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // Backend returned non-JSON (HTML error page from a proxy, etc.)
      payload = null;
    }
  }

  if (!response.ok) {
    const code = (payload && payload.error && payload.error.code) || 'http.' + response.status;
    const message =
      payload && payload.error && payload.error.message
        ? payload.error.message
        : response.status >= 500
          ? 'Serverul are o problemă momentan. Te rugăm să reîncerci în câteva secunde.'
          : 'Cererea nu a putut fi procesată. Reîncearcă sau scrie-ne la salut@crayo.ro.';

    // Capture 5xx to Sentry. 4xx are intentional rejections (validation,
    // not_found) so we skip them — the backend logs handled errors itself.
    if (response.status >= 500) {
      Sentry.captureMessage(`api ${response.status} ${method} ${path}`, {
        level: 'error',
        tags: { kind: 'http', method, path, status: String(response.status), code },
        extra: { textPreview: text?.slice(0, 500) },
      });
    }

    throw new ApiError(code, message, { status: response.status, text });
  }

  return payload;
}

/**
 * Uniform error thrown by every API helper. Components decide what to do
 * with it (toast, retry, navigate). The `code` mirrors the API contract's
 * `error.code` when available, or is prefixed with `network.` / `http.`
 * for transport-level failures.
 */
export class ApiError extends Error {
  constructor(code, message, details) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
  }
}

// ---------------- Session (pre-payment) ----------------

/**
 * Create a session locked to a tier. UTM params are auto-captured from the
 * landing URL via getUtmParams() unless the caller passes an explicit object.
 * UTMs flow through to the Stripe Checkout metadata and the Order record so
 * we can attribute purchases per ad/campaign.
 */
export function createSession(tier, utm) {
  const u = utm === undefined ? getUtmParams() : utm;
  const body = { tier };
  if (u && typeof u === 'object') {
    if (u.utm_source) body.utm_source = String(u.utm_source).slice(0, 200);
    if (u.utm_medium) body.utm_medium = String(u.utm_medium).slice(0, 200);
    if (u.utm_campaign) body.utm_campaign = String(u.utm_campaign).slice(0, 200);
    if (u.utm_content) body.utm_content = String(u.utm_content).slice(0, 200);
    if (u.utm_term) body.utm_term = String(u.utm_term).slice(0, 200);
  }
  return request('/api/session', { method: 'POST', body });
}

export function uploadPhotos(sid, fileList) {
  const form = new FormData();
  for (const file of fileList) form.append('photos', file, file.name);
  return request(`/api/session/${encodeURIComponent(sid)}/photos`, {
    method: 'POST',
    body: form,
  });
}

export function startGeneration(sid) {
  return request(`/api/session/${encodeURIComponent(sid)}/generate`, { method: 'POST' });
}

export function getSession(sid, { signal } = {}) {
  return request(`/api/session/${encodeURIComponent(sid)}`, { signal });
}

/**
 * Start a Stripe Checkout session. The optional `eventId` is a frontend-
 * generated id (ULID via crypto.randomUUID) that the caller fires alongside
 * Meta Pixel's `InitiateCheckout` event; the backend stores it on the
 * Stripe Checkout metadata so the post-payment Conversions API call can
 * use the same id and Meta dedupes the browser + server events.
 *
 * Pixel/CAPI integration ships in a later commit; this signature exists
 * now so the call site lands once and stays stable.
 */
export function startCheckout(sid, eventId) {
  const body = {};
  if (eventId) body.event_id = String(eventId).slice(0, 120);
  return request(`/api/session/${encodeURIComponent(sid)}/checkout`, {
    method: 'POST',
    body: Object.keys(body).length > 0 ? body : undefined,
  });
}

// ---------------- Order (post-payment) ----------------

export function getOrder(oid, { signal } = {}) {
  return request(`/api/order/${encodeURIComponent(oid)}`, { signal });
}

export function takeUpsell(oid) {
  return request(`/api/order/${encodeURIComponent(oid)}/upsell`, { method: 'POST' });
}

// ---------------- Waitlist ----------------

export function joinWaitlist(email, source = 'print') {
  return request('/api/waitlist', { method: 'POST', body: { email, source } });
}

if (typeof window !== 'undefined') {
  window.joinWaitlist = joinWaitlist;
}
